import { useCallback, useMemo, useRef, useState } from 'react';
import type { Layout } from 'react-grid-layout';
import { layoutWithDrill, stripDrill } from './drill-layout';

interface Options {
  /** The dashboard's default tile arrangement. */
  baseLayout: Layout;
  /**
   * Grid key of the tile whose drilldown is open, or `null`. The drill row is
   * derived, never stored — see {@link layoutWithDrill}.
   */
  drillSource?: string | null;
}

interface DashboardLayout {
  /** What to hand `<DashboardGrid>`: the user's arrangement plus any drill row. */
  layout: Layout;
  /** Feed `onLayoutChange` here; the drill row is stripped before storing. */
  onLayoutChange: (next: Layout) => void;
  /** Restore the dashboard's default arrangement. */
  reset: () => void;
  /** Whether the user has moved anything since load (drives the Reset affordance). */
  dirty: boolean;
}

/**
 * Owns a dashboard's tile arrangement.
 *
 * State is **in-memory only** — drag and resize work, but the arrangement
 * resets on reload. That is deliberate for now: persistence belongs with the
 * dashboard API work, and this hook is the single seam it will land in.
 * Nothing outside this file reads or writes layout state, so swapping the
 * `useState` below for a query + mutation changes one file.
 */
/** Positions only — RGL adds resolved defaults (`moved`, `static`, …) to every item. */
function samePositions(a: Layout, b: Layout): boolean {
  if (a.length !== b.length) return false;
  return a.every((item) => {
    const other = b.find((l) => l.i === item.i);
    return (
      !!other &&
      other.x === item.x &&
      other.y === item.y &&
      other.w === item.w &&
      other.h === item.h
    );
  });
}

export function useDashboardLayout({ baseLayout, drillSource = null }: Options): DashboardLayout {
  const [base, setBase] = useState<Layout>(baseLayout);

  /**
   * The arrangement as RGL settled it, captured from its first echo.
   *
   * `dirty` cannot compare against `baseLayout` directly: RGL compacts on
   * mount, so any dashboard whose declared layout has a vertical gap would
   * report dirty before the user touched anything — showing "Reset layout" on
   * an untouched page, where pressing it re-applies a layout RGL immediately
   * re-compacts. Comparing against what RGL actually settled on means dirty
   * tracks user intent rather than the author's formatting.
   */
  const settled = useRef<Layout | null>(null);

  const onLayoutChange = useCallback((next: Layout) => {
    const stripped = stripDrill(next);
    settled.current ??= stripped;
    setBase(stripped);
  }, []);

  const reset = useCallback(() => {
    setBase(baseLayout);
    // Let RGL re-settle the defaults so the next comparison uses its compaction.
    settled.current = null;
  }, [baseLayout]);

  const layout = useMemo(() => layoutWithDrill(base, drillSource), [base, drillSource]);

  const dirty = useMemo(
    () => settled.current !== null && !samePositions(base, settled.current),
    [base],
  );

  return { layout, onLayoutChange, reset, dirty };
}
